import axios from "axios";
import { ClientExceptionType, ServerExceptionType } from "@/models/etc/types";
import { ServerExceptionResult } from "@/models/interfaces/error/server.exception.result";

/**
 * Состояние страницы Клиента/Проекта при неуспешной загрузке сущности:
 * null — обычная ошибка (заглушка не показывается),
 * accessDenied — нет доступа,
 * removed — сущность удалена.
 */
export type PageErrorState = null | "accessDenied" | "removed";

/** Тело ответа сервера об исключении: базовая модель + подтип конкретной сущности. */
export type EntityExceptionResult = ServerExceptionResult & { SubType?: number };

/** Статус, которым бэкенд отвечает и на отсутствие доступа, и на удалённую сущность. */
const ENTITY_ERROR_STATUS = 490;

/** Типы исключений, относящиеся к странице сущности (клиент — 9, проект — 2). */
const ENTITY_TYPES: number[] = [ServerExceptionType.Client, ServerExceptionType.Project];

/** Подтип «удалено» одинаков для клиента и проекта (ClientExceptionType.Removed === ProjectExceptionType.Removed === 2). */
const REMOVED_SUB_TYPE: number = ClientExceptionType.Removed;

/**
 * Определяет состояние страницы Клиента/Проекта по ошибке загрузки сущности.
 * Удаление распознаётся по телу ответа (Type — клиент/проект, SubType — Removed),
 * любой другой 490 трактуется как отсутствие доступа, остальные ошибки — как обычные.
 */
export function resolvePageError(error: unknown): PageErrorState {
    if (!axios.isAxiosError(error) || error.response?.status !== ENTITY_ERROR_STATUS) {
        return null;
    }

    const data = error.response.data as EntityExceptionResult | undefined;

    if (data && typeof data === "object"
        && ENTITY_TYPES.includes(data.Type)
        && data.SubType === REMOVED_SUB_TYPE) {
        return "removed";
    }

    return "accessDenied";
}

/** Тело ответа 490, если ошибка — именно он; иначе null. */
export function getServerException(error: unknown): EntityExceptionResult | null {
    if (!axios.isAxiosError(error) || error.response?.status !== ENTITY_ERROR_STATUS) {
        return null;
    }

    const data = error.response.data as EntityExceptionResult | undefined;
    return data && typeof data === "object" ? data : null;
}
